import { useState, useEffect } from "react";
import { FiCheck, FiX, FiAlertCircle, FiInfo, FiSend } from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";
import { Assessment, LicenseDTO, LicenseTier } from "types/assessmentTypes";
import {
  useGetCompaniesQuery,
  useLazyGetUserOrganizationsQuery,
} from "reduxFiles/api/companyApi";
import Button from "components/button/Button";
import { Company } from "types/companyTypes";
import { useGetLicensesQuery } from "reduxFiles/api/licenseApi";
import SmartSelect, { Option } from "components/select/Select";
import { useLazyAvailableOrganizationLicenseQuery } from "reduxFiles/api/licenseApi";
import { useTranslation } from "react-i18next";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    licenseKey: string,
    selectedCompany: Company,
    organizationId: number,
    handleClose: () => void
  ) => void;
  loading?: boolean;
  title?: string;
  company?: Company;
  showCompanySelect?: boolean;
  assessment?: Assessment | null;
  isTransferMode?: boolean;
};

type LicenseStatus =
  | "idle"
  | "authorized-no-license"
  | "authorized-has-license"
  | "unauthorized";

const CreateAssessmentModal = ({
  isOpen,
  onClose,
  onSubmit,
  loading = false,
  title = "assessment.addAssessment",
  company,
  showCompanySelect = false,
  assessment,
  isTransferMode = false,
}: Props) => {
  const { t } = useTranslation();

  const { data, isLoading: isCompaniesLoading } = useGetCompaniesQuery({
    scope: "user",
    page: 1,
    limit: 100,
  });

  const [triggerOrganizations] = useLazyGetUserOrganizationsQuery();
  const [triggerLicenseCheck, { isLoading: isLicenseChecking }] =
    useLazyAvailableOrganizationLicenseQuery();

  const [selectedLicense, setSelectedLicense] = useState<LicenseDTO | null>(
    null
  );
  const [manualLicenseKey, setManualLicenseKey] = useState("");
  const [isManualMode, setIsManualMode] = useState(false);

  const [selectedCompany, setSelectedCompany] = useState<Company | undefined>(
    company
  );
  const [selectedOrganization, setSelectedOrganization] = useState<
    number | undefined
  >(undefined);

  const [fetchedLicenseData, setFetchedLicenseData] = useState<any | null>(
    null
  );
  const [licenseStatus, setLicenseStatus] = useState<LicenseStatus>("idle");

  const {
    data: licensesData,
    isLoading: isLicensesLoading,
    refetch,
  } = useGetLicensesQuery({
    scope: "user",
    page: 1,
    limit: 100,
    filters: { used: false },
  });

  const licenses: LicenseDTO[] = licensesData?.items || [];
  const companies = data?.items || [];

  const isSameOrganization = () => {
    if (!isTransferMode || !assessment) return false;
    if (selectedOrganization === null) return false;
    return Number(selectedOrganization) === assessment.organization?.id;
  };

  const searchOrganizations = async (text: string): Promise<Option[]> => {
    if (!text || text.length < 2) return [];
    try {
      const res = await triggerOrganizations({
        page: 1,
        limit: 50,
        filters: { name: text },
      }).unwrap();

      return res.items.map((org) => ({
        label: `${org?.name ?? "-"} ${org.province?.name ?? "-"}`,
        value: org.id,
        meta: org,
      }));
    } catch (err) {
      console.error("Error searching organizations:", err);
      return [];
    }
  };

  const resetAllStates = () => {
    setSelectedLicense(null);
    setManualLicenseKey("");
    setIsManualMode(false);
    setSelectedOrganization(undefined);
    setFetchedLicenseData(null);
    setLicenseStatus("idle");
    if (!company) {
      setSelectedCompany(undefined);
    }
  };

  useEffect(() => {
    if (!selectedOrganization || !selectedCompany?.id) {
      setLicenseStatus("idle");
      setFetchedLicenseData(null);
      return;
    }

    const loadLicense = async () => {
      try {
        const res = await triggerLicenseCheck({
          organizationId: Number(selectedOrganization),
          companyId: selectedCompany.id,
        }).unwrap();

        if (res === false) {
          setFetchedLicenseData(null);
          setLicenseStatus("authorized-no-license");
          return;
        }

        if (res?.licenseKey) {
          setFetchedLicenseData(res);
          setLicenseStatus("authorized-has-license");
        }
      } catch (error: any) {
        setFetchedLicenseData(null);
        if (error?.status === 404) {
          setLicenseStatus("unauthorized");
        }
      }
    };

    loadLicense();
  }, [selectedOrganization, selectedCompany, triggerLicenseCheck]);

  useEffect(() => {
    if (isOpen) {
      resetAllStates();
      if (company) {
        setSelectedCompany(company);
      }
      refetch();
    } else {
      resetAllStates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, company, refetch]);

  const handleSubmit = () => {
    if (fetchedLicenseData?.licenseKey && selectedOrganization) {
      if (selectedCompany) {
        onSubmit(
          fetchedLicenseData.licenseKey,
          selectedCompany,
          Number(selectedOrganization),
          handleClose
        );
      }
      return;
    }

    const licenseKey = isManualMode
      ? manualLicenseKey
      : selectedLicense?.licenseKey || "";

    if (licenseKey.trim() && selectedCompany) {
      onSubmit(
        licenseKey,
        selectedCompany,
        selectedOrganization ? Number(selectedOrganization) : undefined,
        handleClose
      );
    }
  };

  const handleClose = () => {
    resetAllStates();
    onClose();
  };

  const companyOptions: Option[] = companies.map((company) => ({
    value: company.id,
    label: company.name,
    meta: company,
  }));

  const licenseOptions: Option[] = licenses.map((license) => ({
    value: license.id,
    label: `${license.licenseKey ?? "-"} ( ${t(
      `group.types.${license.groupType}`
    )} ) ( ${t(`licenses.tiers.${license.tier}`)} )`,
    meta: license,
  }));

  const getSelectedCompany = (value: string | number) => {
    const company = companies.find((c) => c.id === value);
    return company;
  };

  const getSelectedLicense = (value: string | number) => {
    const license = licenses.find((l) => l.id === value);
    return license;
  };

  const isOrganizationDisabled = () => {
    if (!selectedCompany) return true;
    if (isCompaniesLoading) return true;
    return false;
  };

  const isSubmitDisabled = () => {
    if (loading) return true;
    if (!selectedCompany) return true;

    if (isTransferMode && !selectedOrganization) return true;
    if (isTransferMode && assessment && isSameOrganization()) {
      return true;
    }

    if (showCompanySelect && !company && companyOptions.length === 0)
      return true;

    if (selectedOrganization) {
      switch (licenseStatus) {
        case "unauthorized":
          return true;

        case "authorized-has-license":
          return false;

        case "authorized-no-license":
          if (isManualMode) {
            return manualLicenseKey.trim().length === 0;
          }
          return !selectedLicense;

        case "idle":
        default:
          if (isManualMode) {
            return manualLicenseKey.trim().length === 0;
          }
          return !selectedLicense;
      }
    }

    if (isManualMode) {
      return manualLicenseKey.trim().length === 0;
    }
    return !selectedLicense;
  };

  const shouldShowLicenseSelection = () => {
    if (selectedOrganization && licenseStatus === "authorized-has-license") {
      return false;
    }
    return true;
  };

  const shouldShowLicenseInfo = () => {
    return (
      selectedOrganization &&
      licenseStatus === "authorized-has-license" &&
      !!fetchedLicenseData?.licenseKey
    );
  };

  const shouldShowOrganizationMessages = () => {
    return !!selectedOrganization;
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
          className="bg-black/50 fixed inset-0 backdrop-blur-sm"
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.92, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.92, y: 30 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-navy-800"
        >
          <div className="border-b border-gray-100 px-6 py-4 dark:border-white/10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div
                  className={`rounded-xl bg-gradient-to-br ${
                    isTransferMode
                      ? "from-purple-500 to-pink-600"
                      : "from-blue-500 to-purple-600"
                  } p-2 shadow-lg ${
                    isTransferMode
                      ? "shadow-purple-500/20"
                      : "shadow-blue-500/20"
                  }`}
                >
                  {isTransferMode ? (
                    <FiSend className="h-4 w-4 text-white" />
                  ) : (
                    <FiCheck className="h-4 w-4 text-white" />
                  )}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t(title)}
                </h3>
              </div>
              <button
                onClick={handleClose}
                className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-white/10"
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="space-y-5 p-6">
            {showCompanySelect && !company && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.02 }}
              >
                <SmartSelect
                  mode="static"
                  options={companyOptions}
                  value={selectedCompany?.id}
                  onChange={(val) => {
                    const company = getSelectedCompany(val);
                    setSelectedLicense(null);
                    setManualLicenseKey("");
                    setIsManualMode(false);
                    setSelectedOrganization(undefined);
                    setFetchedLicenseData(null);
                    setLicenseStatus("idle");
                    setSelectedCompany(company);
                  }}
                  label={t("assessment.companyId")}
                  clearable={true}
                  disabled={isCompaniesLoading}
                />
                {isCompaniesLoading && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {t("assessment.loadingCompanies")}
                  </p>
                )}
              </motion.div>
            )}

            {company && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.02 }}
              >
                <div className="rounded-lg bg-blue-50 p-3 dark:bg-blue-900/20">
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    <span className="font-medium">
                      {t("assessment.selectedCompany")}:
                    </span>{" "}
                    {company.name}
                  </p>
                  {isTransferMode && assessment && (
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      <span className="font-medium">
                        {t("assessment.currentOrganization")}:
                      </span>{" "}
                      {assessment.organization?.name || "-"}
                    </p>
                  )}
                </div>
              </motion.div>
            )}

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
            >
              <div className="relative">
                <SmartSelect
                  mode="dynamic"
                  value={selectedOrganization}
                  onChange={(val) => {
                    if (Number(val) === Number(selectedOrganization)) {
                      return;
                    }
                    setSelectedOrganization(Number(val));
                    setFetchedLicenseData(null);
                    setLicenseStatus("idle");
                    setSelectedLicense(null);
                    setManualLicenseKey("");
                  }}
                  onSearch={searchOrganizations}
                  label={
                    isTransferMode
                      ? `${t("assessment.organization")} *`
                      : t("assessment.organization")
                  }
                  clearable={true}
                  minSearchLength={2}
                  debounce={300}
                  disabled={isOrganizationDisabled()}
                />

                {isTransferMode && !selectedOrganization && (
                  <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                    * {t("assessment.organizationRequired")}
                  </p>
                )}

                {isOrganizationDisabled() && (
                  <p className="mt-1.5 flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-400">
                    <FiAlertCircle className="h-3.5 w-3.5" />
                    <span>{t("assessment.selectCompanyFirst")}</span>
                  </p>
                )}

                {isTransferMode && assessment && isSameOrganization() && (
                  <p className="mt-1.5 flex items-center gap-1.5 text-xs text-red-600 dark:text-red-400">
                    <FiAlertCircle className="h-3.5 w-3.5" />
                    <span>
                      {t("assessment.cannotTransferToSameOrganization")}
                    </span>
                  </p>
                )}
              </div>
            </motion.div>

            {selectedOrganization && selectedCompany && isLicenseChecking && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center justify-center rounded-lg border-2 border-dashed border-gray-200 py-4 dark:border-white/10"
              >
                <div className="loader h-5 w-5 text-blue-700" />
                <span className="mr-2 text-sm text-gray-500 dark:text-gray-400">
                  {t("assessment.checkingLicense")}
                </span>
              </motion.div>
            )}

            {shouldShowLicenseInfo() && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08 }}
              >
                <div className="rounded-lg border-2 border-green-200 bg-green-50 p-4 dark:border-green-800/30 dark:bg-green-900/20">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 rounded-full bg-green-100 p-1.5 dark:bg-green-800/30">
                      <FiCheck className="h-4 w-4 text-green-600 dark:text-green-400" />
                    </div>
                    <div className="flex-1 space-y-2">
                      <p className="font-medium text-green-700 dark:text-green-300">
                        {t("assessment.licenseAvailable")}
                      </p>

                      <div className="space-y-1.5 text-sm">
                        {fetchedLicenseData.scope && (
                          <div className="flex items-center gap-2">
                            <span className="text-gray-600 dark:text-gray-400">
                              {t("licenses.scope")}:
                            </span>
                            <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                              {t(`licenses.scopes.${fetchedLicenseData.scope}`)}
                            </span>
                          </div>
                        )}

                        {fetchedLicenseData.tier && (
                          <div className="flex items-center gap-2">
                            <span className="text-gray-600 dark:text-gray-400">
                              {t("licenses.tier")}:
                            </span>
                            <span
                              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium text-white ${
                                fetchedLicenseData.tier === LicenseTier.SPECIAL
                                  ? "bg-gradient-to-r from-purple-500 to-pink-500"
                                  : "bg-gradient-to-r from-blue-500 to-indigo-500"
                              }`}
                            >
                              {t(`licenses.tiers.${fetchedLicenseData.tier}`)}
                            </span>
                          </div>
                        )}

                        <div className="flex items-center gap-2">
                          <span className="text-gray-600 dark:text-gray-400">
                            {t("assessment.organization")}:
                          </span>
                          <span className="font-medium text-gray-700 dark:text-gray-300">
                            {fetchedLicenseData.organization?.name ?? "-"}
                          </span>
                        </div>
                      </div>

                      <div className="mt-2 rounded-lg bg-green-100/50 p-2 dark:bg-green-800/20">
                        <p className="flex items-center gap-1 text-xs text-green-700 dark:text-green-300">
                          <FiInfo className="inline h-3.5 w-3.5" />
                          {isTransferMode
                            ? t("assessment.licenseAvailableTransferHint")
                            : t("assessment.licenseAvailableCreateHint")}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {shouldShowOrganizationMessages() &&
              licenseStatus === "unauthorized" && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 }}
                >
                  <div className="rounded-lg border-2 border-red-200 bg-red-50 p-4 dark:border-red-800/30 dark:bg-red-900/20">
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 rounded-full bg-red-100 p-1.5 dark:bg-red-800/30">
                        <FiAlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-red-700 dark:text-red-300">
                          {t("assessment.unauthorizedTitle")}
                        </p>
                        <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                          {t("assessment.unauthorizedDescription")}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

            {shouldShowOrganizationMessages() &&
              licenseStatus === "authorized-no-license" && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 }}
                >
                  <div className="rounded-lg border-2 border-orange-200 bg-orange-50 p-4 dark:border-orange-800/30 dark:bg-orange-900/20">
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 rounded-full bg-orange-100 p-1.5 dark:bg-orange-800/30">
                        <FiAlertCircle className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-orange-700 dark:text-orange-300">
                          {t("assessment.noLicenseAvailable")}
                        </p>
                        <p className="mt-1 text-sm text-orange-600 dark:text-orange-400">
                          {t("assessment.noLicenseDescription")}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

            {shouldShowLicenseSelection() && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <div className="mb-1.5 flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t("assessment.licenseKey")}
                    <span className="mr-1 text-red-500">*</span>
                  </label>
                  <button
                    onClick={() => {
                      setIsManualMode(!isManualMode);
                      setSelectedLicense(null);
                    }}
                    className="rounded-lg bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-600 transition-colors hover:bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400 dark:hover:bg-blue-900/30"
                  >
                    {isManualMode
                      ? t("assessment.selectFromList")
                      : t("assessment.enterManually")}
                  </button>
                </div>

                {isLicensesLoading ? (
                  <div className="flex items-center justify-center rounded-xl border-2 border-dashed border-gray-200 py-8 dark:border-white/10">
                    <div className="loader h-5 w-5 text-blue-700" />
                    <span className="mr-2 text-sm text-gray-500 dark:text-gray-400">
                      {t("assessment.loadingLicenses")}
                    </span>
                  </div>
                ) : isManualMode ? (
                  <div>
                    <input
                      type="text"
                      value={manualLicenseKey}
                      onChange={(e) => setManualLicenseKey(e.target.value)}
                      placeholder={t("assessment.enterLicenseKey")}
                      className="w-full rounded-xl border-2 border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-white/10 dark:bg-navy-700 dark:text-white dark:placeholder:text-gray-500"
                    />
                    <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                      {t("assessment.manualLicenseHint")}
                    </p>
                  </div>
                ) : (
                  <SmartSelect
                    mode="static"
                    options={licenseOptions}
                    value={selectedLicense?.id}
                    onChange={(val) => {
                      const license = getSelectedLicense(val);
                      setSelectedLicense(license || null);
                    }}
                    clearable={true}
                    disabled={licenseOptions.length === 0}
                  />
                )}
              </motion.div>
            )}
          </div>

          <div className="border-t border-gray-100 bg-gray-50/30 px-6 py-4 dark:border-white/5 dark:bg-white/5">
            <div className="flex gap-3">
              <Button onClick={handleClose} variant="error" className="flex-1">
                {t("buttons.cancel")}
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={isSubmitDisabled()}
                variant={isTransferMode ? "info" : "success"}
                className="flex-1"
                loading={loading}
                startIcon={
                  isTransferMode ? (
                    <FiSend className="h-5 w-5 text-white" />
                  ) : (
                    <FiCheck className="h-5 w-5 text-white" />
                  )
                }
              >
                {isTransferMode ? t("buttons.transfer") : t("buttons.create")}
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default CreateAssessmentModal;
